import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
    const [searchParams] = useSearchParams();
    const selectedTier = searchParams.get('tier');
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        const result = await register(email, password, name);
        
        if (result.success) {
            toast.success('Welcome to Methode 4 Life!');
            if (selectedTier) {
                navigate(`/membership?tier=${selectedTier}`);
            } else {
                navigate('/membership');
            }
        } else {
            toast.error(result.error);
        }
        
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] flex" data-testid="register-page">
            {/* Left side - Image */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1772404245872-b9cdc34bfbcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxwZW9wbGUlMjBnYXRoZXJpbmclMjBtZWRpdGF0aW9uJTIwcmV0cmVhdCUyMGx1eHVyeXxlbnwwfHx8fDE3NzYyNTQyNDZ8MA&ixlib=rb-4.1.0&q=85"
                    alt="Retreat gathering"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#5B2D8E]/20"></div>
                <div className="absolute bottom-12 left-12 right-12 text-white">
                    <h2 className="font-serif text-4xl mb-4">Join the Circle</h2>
                    <p className="font-sans text-lg text-white/80">Begin your journey of remembering who you are.</p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="block mb-12" data-testid="register-logo">
                        <h1 className="font-serif text-2xl text-[#1A1A2E]">Methode 4 Life</h1>
                    </Link>

                    <div className="mb-8">
                        <h2 className="font-serif text-3xl text-[#1A1A2E] mb-2">Create Account</h2>
                        <p className="font-sans text-[#4A4A68]">
                            Already have an account?{' '}
                            <Link to="/login" className="text-[#5B2D8E] hover:underline" data-testid="register-login-link">
                                Sign In
                            </Link>
                        </p>
                        {selectedTier && (
                            <p className="font-sans text-sm text-[#5B2D8E] mt-2">
                                You selected: <span className="font-medium uppercase">{selectedTier}</span> membership
                            </p>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                Full Name
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full"
                                placeholder="Your name"
                                required
                                data-testid="register-name"
                            />
                        </div>

                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                Email Address
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full"
                                placeholder="you@example.com"
                                required
                                data-testid="register-email"
                            />
                        </div>

                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pr-10"
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    data-testid="register-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A68] hover:text-[#5B2D8E]"
                                    data-testid="register-toggle-password"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full"
                                placeholder="••••••••"
                                required
                                minLength={6}
                                data-testid="register-confirm-password"
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn-primary w-full"
                            disabled={loading}
                            data-testid="register-submit"
                        >
                            {loading ? 'Creating account...' : 'Join the Circle'}
                        </button>

                        <p className="font-sans text-xs text-[#4A4A68] text-center">
                            By creating an account, you agree to our Terms of Service and Privacy Policy.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
